import { useQueryFactory } from './useQueryFactory'
import { subscriptionService } from '@/services/subscription'
import { queryKeys } from '@/lib/query-keys'
import type { StripeProduct } from '@/services/subscription'
import { SubscriptionType, StatutType } from '@/types/subscription'
import type { StripeInterval } from '@/constants/subscription'

export interface ProcessedSubscription {
  id: string
  name: string
  description: string | null
  type: SubscriptionType
  interval: StripeInterval
  licenses?: number | string
  metadataType?: string
  price: {
    id: string
    amount: number
    currency: string
    discount_percent?: string
  }
}

function processSubscriptions(products: StripeProduct[]): ProcessedSubscription[] {
  return products
    .filter(product => product.active && product.prices.length > 0)
    .flatMap(product => {
      const statutLower = product.metadata.statut?.toLowerCase()
      const type: SubscriptionType =
        statutLower === StatutType.GROUPE.toLowerCase()
          ? SubscriptionType.STRUCTURE
          : SubscriptionType.INDIVIDUAL

      return product.prices
        .filter(price => price.active && price.type === 'recurring')
        .map<ProcessedSubscription>(price => {
          const discountPercent =
            price.discount_percent ??
            (product.metadata as { discount_percent?: string | number })?.discount_percent
          return {
            id: product.id,
            name: product.name,
            description: product.description,
            type,
            interval: price.recurring.interval,
            licenses: product.metadata.licenses ?? product.metadata.licences,
            metadataType: product.metadata.type,
            price: {
              id: price.id,
              amount: price.unit_amount,
              currency: price.currency,
              ...(discountPercent != null &&
                discountPercent !== '' && { discount_percent: String(discountPercent) }),
            },
          }
        })
    })
}

export const useSubscriptions = () => {
  const queryKey = queryKeys.subscriptions.all

  const query = useQueryFactory({
    queryKey,
    queryFn: () => subscriptionService.getSubscriptions(),
    enabled: true,
    staleTime: 10 * 60 * 1000, // 10 minutes
  })

  const processedSubscriptions = query.data?.results ? processSubscriptions(query.data.results) : []

  return {
    subscriptions: processedSubscriptions,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}
